import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Sparkles, CheckCircle2, ArrowRight, Megaphone, Share2, Zap, Lock, Wand2, Bot, LineChart, FileDown } from 'lucide-react';
import { SOCIAL_PLATFORMS, ADS_PLATFORMS, PlatformDefinition } from '../../constants';
import { socialService } from '../../services/socialService';
import { useProject } from '../../contexts/ProjectContext';

/**
 * Hero de onboarding de conexão de contas — mostrado no dashboard até o usuário
 * conectar pelo menos 1 plataforma de Ads + 1 social. Depois disso, some e dá
 * lugar ao AIAssistantHero original com recomendações.
 *
 * Visual inspirado no onboarding de 5 steps do devskin-vendas2: glass morphism,
 * gradiente vibrante, progress bar animada e cards que acendem quando conectados.
 */
interface Props {
  userName: string;
  /** onReady: quando o hero decide não mostrar (tudo conectado), dispara pro pai renderizar outro conteúdo */
  onReady?: (allReady: boolean) => void;
}

interface AccountLite {
  platform: string;
  connected: boolean;
}

const getTimeGreetingKey = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return 'goodMorning';
  if (hour < 18) return 'goodAfternoon';
  return 'goodEvening';
};

const ConnectAccountsHero: React.FC<Props> = ({ userName, onReady }) => {
  const { t } = useTranslation();
  const { currentProject } = useProject();
  const [accounts, setAccounts] = useState<AccountLite[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await socialService.getAccountsWithAds(currentProject?.id);
        const list = (r.data || []).map((a: any) => ({ platform: a.platform, connected: !!a.connected }));
        setAccounts(list);
      } catch {
        setAccounts([]);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject?.id]);

  const isConnected = (platformId: string): boolean => {
    if (!Array.isArray(accounts)) return false;
    // Normaliza: algumas plataformas têm aliases no backend
    const aliases: Record<string, string[]> = {
      facebook_ads: ['facebook_ads', 'meta_ads', 'facebook'],
      google_ads: ['google_ads'],
      linkedin_ads: ['linkedin_ads'],
    };
    const candidates = aliases[platformId] || [platformId];
    return accounts.some(a => a.connected && candidates.includes(a.platform));
  };

  const adsConnected = ADS_PLATFORMS.filter(p => isConnected(p.id)).length;
  const socialConnected = SOCIAL_PLATFORMS.filter(p => isConnected(p.id)).length;
  const totalConnected = adsConnected + socialConnected;
  const totalPlatforms = ADS_PLATFORMS.length + SOCIAL_PLATFORMS.length;
  const percent = totalPlatforms > 0 ? (totalConnected / totalPlatforms) * 100 : 0;
  const mvpReady = adsConnected >= 1 && socialConnected >= 1;

  useEffect(() => {
    if (mvpReady && onReady) onReady(true);
  }, [mvpReady, onReady]);

  const greetingKey = getTimeGreetingKey();
  const greeting = t(`dashboard.${greetingKey}`, greetingKey === 'goodMorning' ? 'Good morning' : greetingKey === 'goodAfternoon' ? 'Good afternoon' : 'Good evening');

  // Progress ring SVG — 60px diameter, stroke 5px
  const radius = 26;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  const renderPlatformChip = (p: PlatformDefinition, connected: boolean) => (
    <Link
      key={p.id}
      to="/settings/integrations"
      title={connected
        ? t('dashboard.onboarding.connected', '{{name}} conectado', { name: p.name })
        : t('dashboard.onboarding.connect', 'Conectar {{name}}', { name: p.name })}
      className={`group relative min-w-0 flex items-center gap-2 px-2 sm:px-3 py-2 rounded-xl border transition-all ${
        connected
          ? 'bg-white/20 border-white/30 backdrop-blur-sm'
          : 'bg-white/5 border-white/10 hover:bg-white/15 hover:border-white/20 backdrop-blur-sm'
      }`}
    >
      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${p.color} flex items-center justify-center flex-shrink-0 text-white ${connected ? '' : 'opacity-70 group-hover:opacity-100'} transition-opacity`}>
        <div className="w-4 h-4">{p.icon}</div>
      </div>
      <span className={`min-w-0 text-sm font-medium truncate ${connected ? 'text-white' : 'text-white/80 group-hover:text-white'}`}>
        {p.name}
      </span>
      {connected ? (
        <CheckCircle2 className="w-4 h-4 text-emerald-300 ml-auto flex-shrink-0" />
      ) : (
        <span className="hidden sm:inline ml-auto text-[10px] font-semibold uppercase tracking-wider text-white/60 group-hover:text-white/90">
          {t('dashboard.onboarding.connect_short', 'Conectar')}
        </span>
      )}
    </Link>
  );

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-600 via-purple-600 to-pink-600 p-4 sm:p-8">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10">
        {/* Top: greeting + progress ring */}
        <div className="flex items-start justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="hidden sm:flex w-12 h-12 rounded-xl bg-white/20 backdrop-blur items-center justify-center flex-shrink-0">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-3xl font-bold text-white break-words">
                {greeting}, {userName}!
              </h1>
              <p className="text-white/85 text-sm sm:text-base">
                {mvpReady
                  ? t('dashboard.onboarding.almostThere', 'Quase lá! Conecte mais plataformas pra liberar toda a inteligência da IA.')
                  : t('dashboard.onboarding.intro', 'Conecte suas contas pra desbloquear IA, relatórios e automações')}
              </p>
            </div>
          </div>

          {/* Progress ring */}
          <div className="relative w-14 h-14 sm:w-16 sm:h-16 flex-shrink-0">
            <svg className="w-14 h-14 sm:w-16 sm:h-16 -rotate-90" viewBox="0 0 60 60">
              <circle cx="30" cy="30" r={radius} stroke="rgba(255,255,255,0.15)" strokeWidth="5" fill="none" />
              <circle
                cx="30" cy="30" r={radius}
                stroke="white" strokeWidth="5" strokeLinecap="round" fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                className="transition-all duration-700 ease-out"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white font-bold text-sm">{Math.round(percent)}%</span>
            </div>
          </div>
        </div>

        {/* Step indicators — 2 grupos: Ads e Social */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-2">
          {/* Ads Platforms */}
          <div className="bg-white/[0.08] backdrop-blur-md rounded-2xl border border-white/10 p-4">
            <div className="flex items-center justify-between mb-3 sm:mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-400/20 flex items-center justify-center">
                  <Megaphone className="w-4 h-4 text-amber-200" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">
                    {t('dashboard.onboarding.adsTitle', 'Plataformas de Ads')}
                  </div>
                  <div className="text-[11px] text-white/60">
                    {t('dashboard.onboarding.connectedCount', '{{n}} de {{total}} conectadas', { n: adsConnected, total: ADS_PLATFORMS.length })}
                  </div>
                </div>
              </div>
              {adsConnected >= 1 && (
                <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-400/20 text-emerald-200 px-2 py-0.5 rounded-full">
                  {t('dashboard.onboarding.readyFlag', '✓ Pronto')}
                </span>
              )}
            </div>
            <p className="hidden sm:block text-[11px] text-white/65 leading-relaxed mb-3">
              {t('dashboard.onboarding.adsDesc', 'Importa campanhas reais, sincroniza métricas e permite editar anúncios direto daqui sem abrir o painel nativo.')}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-1 gap-2">
              {ADS_PLATFORMS.map(p => renderPlatformChip(p, isConnected(p.id)))}
            </div>
          </div>

          {/* Social Platforms */}
          <div className="bg-white/[0.08] backdrop-blur-md rounded-2xl border border-white/10 p-4">
            <div className="flex items-center justify-between mb-3 sm:mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-sky-400/20 flex items-center justify-center">
                  <Share2 className="w-4 h-4 text-sky-200" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">
                    {t('dashboard.onboarding.socialTitle', 'Redes Sociais')}
                  </div>
                  <div className="text-[11px] text-white/60">
                    {t('dashboard.onboarding.connectedCount', '{{n}} de {{total}} conectadas', { n: socialConnected, total: SOCIAL_PLATFORMS.length })}
                  </div>
                </div>
              </div>
              {socialConnected >= 1 && (
                <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-400/20 text-emerald-200 px-2 py-0.5 rounded-full">
                  {t('dashboard.onboarding.readyFlag', '✓ Pronto')}
                </span>
              )}
            </div>
            <p className="hidden sm:block text-[11px] text-white/65 leading-relaxed mb-3">
              {t('dashboard.onboarding.socialDesc', 'Agenda posts em todas as redes de uma vez, responde comentários com IA e gera criativos otimizados pra cada canal.')}
            </p>
            <div className="grid grid-cols-2 gap-2 max-h-[220px] overflow-y-auto pr-1 scrollbar-hide">
              {SOCIAL_PLATFORMS.map(p => renderPlatformChip(p, isConnected(p.id)))}
            </div>
          </div>
        </div>

        {/* Benefits unlocked — explica o que cada combinação libera */}
        <div className="hidden sm:block mt-4 bg-white/[0.05] backdrop-blur-md rounded-xl border border-white/10 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-yellow-300" />
            <span className="text-xs font-bold uppercase tracking-wider text-white/90">
              {t('dashboard.onboarding.unlocked', 'O que você desbloqueia')}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            {[
              { icon: LineChart, labelKey: 'dashboard.onboarding.benefitMetrics', label: 'Métricas em tempo real', unlocked: adsConnected >= 1, reqKey: 'dashboard.onboarding.needAds', reqLabel: '1+ Ads' },
              { icon: Wand2, labelKey: 'dashboard.onboarding.benefitCreatives', label: 'IA de criativos', unlocked: totalConnected >= 1, reqKey: 'dashboard.onboarding.needAny', reqLabel: '1+ conta' },
              { icon: Bot, labelKey: 'dashboard.onboarding.benefitAutomation', label: 'Automações e bots', unlocked: socialConnected >= 1, reqKey: 'dashboard.onboarding.needSocial', reqLabel: '1+ social' },
              { icon: FileDown, labelKey: 'dashboard.onboarding.benefitReports', label: 'Relatórios consolidados', unlocked: mvpReady, reqKey: 'dashboard.onboarding.needMvp', reqLabel: '1 Ads + 1 social' },
            ].map((b, idx) => {
              const Icon = b.icon;
              return (
                <div
                  key={idx}
                  className={`flex items-start gap-2 p-2 rounded-lg transition-all ${
                    b.unlocked
                      ? 'bg-emerald-400/15 border border-emerald-400/30'
                      : 'bg-white/[0.03] border border-white/10'
                  }`}
                >
                  <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${
                    b.unlocked ? 'bg-emerald-400/25 text-emerald-200' : 'bg-white/10 text-white/40'
                  }`}>
                    {b.unlocked ? <Icon className="w-3.5 h-3.5" /> : <Lock className="w-3 h-3" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`text-[11px] font-semibold leading-tight ${b.unlocked ? 'text-white' : 'text-white/60'}`}>
                      {t(b.labelKey, b.label)}
                    </div>
                    <div className={`text-[10px] leading-tight mt-0.5 ${b.unlocked ? 'text-emerald-200' : 'text-white/45'}`}>
                      {b.unlocked ? t('dashboard.onboarding.unlockedFlag', '✓ Liberado') : t(b.reqKey, `Precisa: ${b.reqLabel}`)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA bar */}
        <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white/[0.08] backdrop-blur-md rounded-xl border border-white/10 p-3 sm:p-4">
          <div className="flex items-center gap-3">
            <Zap className="w-5 h-5 text-yellow-300 flex-shrink-0" />
            <div>
              <div className="text-sm font-semibold text-white">
                {totalConnected === 0
                  ? t('dashboard.onboarding.ctaStart', 'Comece conectando sua primeira plataforma')
                  : mvpReady
                    ? t('dashboard.onboarding.ctaUnlock', 'Conecte mais plataformas pra maximizar alcance')
                    : t('dashboard.onboarding.ctaMvp', '1 ads + 1 social libera tudo da plataforma')}
              </div>
              <div className="text-[11px] text-white/70 mt-0.5">
                {loading
                  ? t('common.loading', 'Carregando...')
                  : t('dashboard.onboarding.ctaSub', '{{n}} de {{total}} conectadas · Leva 30s cada', { n: totalConnected, total: totalPlatforms })}
              </div>
            </div>
          </div>
          <Link
            to="/settings/integrations"
            className="w-full sm:w-auto justify-center inline-flex items-center gap-2 bg-white text-gray-900 hover:bg-gray-100 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
          >
            {t('dashboard.onboarding.goToSettings', 'Ir para Configurações')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ConnectAccountsHero;
